import apiClient from './client';

// NOTE: `baseURL` override mirrors the other v2 modules (criteria/skillsTaxonomy)
// until apiClient is refactored to a `/api` base with versioned paths.
const V2_BASE = '/api/v2';
const ROOT = '/skill-trials';

// ---------------------------------------------------------------------------
// Types — snake_case, matching the response shape after the client's
// camelCase→snake_case conversion (see api/client.ts). Request bodies are
// written snake_case too; the request interceptor converts them to camelCase.
// ---------------------------------------------------------------------------

export type TrialKind = 'REQUIREMENT' | 'RESPONSIBILITY';
export type TrialLanguage = 'DE' | 'EN' | 'UNKNOWN';
export type SkillFacet = 'TECHNOLOGY' | 'METHOD' | 'LANGUAGE' | 'FIELD' | 'COMPETENCE';

export const FACETS: SkillFacet[] = ['COMPETENCE', 'TECHNOLOGY', 'METHOD', 'FIELD', 'LANGUAGE'];

export interface TrialSetSummary {
  id: string;
  label: string;
  created_at: string;
}

/** One criterion to label cold — text and strata only, never any arm output (§6.7). */
export interface BlindTask {
  criterion_id: string;
  criterion_version: string;
  kind: TrialKind;
  language: TrialLanguage;
  text: string;
}

export interface BlindBatch {
  set_id: string;
  total: number;
  labelled: number;
  pending: BlindTask[];
}

export interface ConceptInput {
  expected_facet: SkillFacet;
  surfaces: string[];
}

export interface BlindLabelsSaved {
  criterion_id: string;
  concepts: number;
}

export interface ConstructionResult {
  set_id: string;
  members: number;
  blind_reserved: number;
}

/** Trial sets, for the surface's set picker. */
export async function fetchTrialSets(): Promise<TrialSetSummary[]> {
  const { data } = await apiClient.get(`${ROOT}/sets`, { baseURL: V2_BASE });
  return data as TrialSetSummary[];
}

/** The blind batch for a set: total reserved, how many labelled, and the pending tasks. */
export async function fetchBlindBatch(setId: string): Promise<BlindBatch> {
  const { data } = await apiClient.get(`${ROOT}/sets/${setId}/blind-tasks`, { baseURL: V2_BASE });
  return data as BlindBatch;
}

/**
 * Seed a stratified trial set from the corpus (§7): balances kind × language and
 * reserves the blind sample, leaving content_class for hand-labelling.
 */
export async function constructSet(body: {
  label: string;
  notes?: string;
  per_cell_target?: number;
  blind_per_kind?: number;
  seed?: number;
}): Promise<ConstructionResult> {
  const { data } = await apiClient.post(`${ROOT}/sets/construct`, body, { baseURL: V2_BASE });
  return data as ConstructionResult;
}

// --- adjudication (§6) ------------------------------------------------------

export type RunStatus = 'RUNNING' | 'COMPLETE' | 'FAILED' | 'CANCELLED';

export interface RunView {
  id: string;
  label: string;
  status: RunStatus;
  criterion_set_id: string;
  started_at: string;
  finished_at: string | null;
}

export interface ArmScore {
  run_id: string;
  label: string;
  produced: number;
  produced_hits: number;
  precision: number;
  gold_concepts: number;
  gold_hits: number;
  recall: number;
  facet_total: number | null;
  facet_correct: number | null;
  facet_accuracy: number | null;
}

export interface Scoreboard {
  adjudicated_criteria: number;
  gold_concepts: number;
  arm_a: ArmScore;
  arm_b: ArmScore;
}

export type TrialExclusionReason = 'COMPANY_REQUIREMENT' | 'TENDER_REQUIREMENT' | 'NOT_SKILL_RELATED';

export interface PendingComparison {
  set_id: string;
  total: number;
  adjudicated: number;
  agreement_skipped: number;
  excluded: number;
  pending: string[];
}

export interface ComparisonOverview {
  pending: PendingComparison;
  scoreboard: Scoreboard;
}

export interface ProposedRow {
  surface: string;
  facet: SkillFacet | null;
  from_a: boolean;
  from_b: boolean;
}

export interface EmptyPass {
  arm: string;
  facet: SkillFacet;
}

export interface RunSummary {
  run_id: string;
  status: RunStatus;
  observations: number;
  would_orphan: number;
  would_resolve: number;
}

/** Start an arm over a set. Omit pass_set to run the configured five v4 passes (§9). */
export async function startRun(body: {
  label: string;
  criterion_set_id: string;
  pass_set?: { pass: string | null; prompt_file: string; model: string | null }[];
}): Promise<RunView> {
  const { data } = await apiClient.post(`${ROOT}/runs`, body, { baseURL: V2_BASE });
  return data as RunView;
}

/** Build the v3 baseline for a set from existing production observations — no LLM calls. */
export async function materializeBaseline(body: { set_id: string; label?: string }): Promise<RunView> {
  const { data } = await apiClient.post(`${ROOT}/runs/baseline`, body, { baseURL: V2_BASE });
  return data as RunView;
}

/** Request cancellation of a running arm; it stops at the next criterion boundary. */
export async function cancelRun(runId: string): Promise<RunView> {
  const { data } = await apiClient.post(`${ROOT}/runs/${runId}/cancel`, {}, { baseURL: V2_BASE });
  return data as RunView;
}

/** Permanently delete a run and its observations (must not be RUNNING). Gold is untouched. */
export async function deleteRun(runId: string): Promise<void> {
  await apiClient.delete(`${ROOT}/runs/${runId}`, { baseURL: V2_BASE });
}

/** Observation count + curation-load forecast for a run (§5.2). */
export async function fetchRunSummary(runId: string): Promise<RunSummary> {
  const { data } = await apiClient.get(`${ROOT}/runs/${runId}/summary`, { baseURL: V2_BASE });
  return data as RunSummary;
}

export interface ScoreCell {
  origin: 'ADJUDICATED' | 'BLIND';
  cell: string;
  arm_a: ArmScore;
  arm_b: ArmScore;
}

export interface ScoreReport {
  run_a: string;
  run_b: string;
  run_a_label: string;
  run_b_label: string;
  cells: ScoreCell[];
}

export interface CriterionAdjudication {
  criterion_id: string;
  criterion_version: string;
  kind: string;
  language: string;
  text: string;
  already_adjudicated: boolean;
  rows: ProposedRow[];
  empty_passes: EmptyPass[];
}

/** Runs, optionally filtered to a set — the arms available to compare. */
export async function fetchRuns(setId?: string): Promise<RunView[]> {
  const { data } = await apiClient.get(`${ROOT}/runs`, {
    baseURL: V2_BASE,
    // Query params are not key-converted by the client; match the backend @RequestParam name.
    params: setId ? { setId } : undefined,
  });
  return data as RunView[];
}

export async function fetchComparison(runA: string, runB: string): Promise<ComparisonOverview> {
  const { data } = await apiClient.get(`${ROOT}/comparisons/${runA}/${runB}`, { baseURL: V2_BASE });
  return data as ComparisonOverview;
}

/** The full §8 report: precision/recall per cell, per population, per arm. */
export async function fetchReport(runA: string, runB: string): Promise<ScoreReport> {
  const { data } = await apiClient.get(`${ROOT}/comparisons/${runA}/${runB}/report`, { baseURL: V2_BASE });
  return data as ScoreReport;
}

export async function fetchCriterionAdjudication(
  runA: string,
  runB: string,
  criterionId: string
): Promise<CriterionAdjudication> {
  const { data } = await apiClient.get(`${ROOT}/comparisons/${runA}/${runB}/criteria/${criterionId}`, {
    baseURL: V2_BASE,
  });
  return data as CriterionAdjudication;
}

/** Exclude a criterion — not a skill requirement (company/tender requirement). Drops it from pending + scoring. */
export async function excludeCriterion(
  runA: string,
  runB: string,
  body: { criterion_id: string; reason: TrialExclusionReason }
): Promise<void> {
  await apiClient.post(`${ROOT}/comparisons/${runA}/${runB}/exclude`, body, { baseURL: V2_BASE });
}

/** Clear all adjudications for a set so it can be re-adjudicated (blind gold is kept). */
export async function clearAdjudications(setId: string): Promise<{ deleted: number }> {
  const { data } = await apiClient.delete(`${ROOT}/sets/${setId}/adjudications`, { baseURL: V2_BASE });
  return data as { deleted: number };
}

/** Submit the kept gold row for a criterion (origin ADJUDICATED). Judge = authenticated user. */
export async function submitAdjudication(
  runA: string,
  runB: string,
  body: { criterion_id: string; criterion_version: string; concepts: ConceptInput[] }
): Promise<{ criterion_id: string; concepts: number }> {
  const { data } = await apiClient.post(`${ROOT}/comparisons/${runA}/${runB}/adjudicate`, body, {
    baseURL: V2_BASE,
  });
  return data as { criterion_id: string; concepts: number };
}

/** Write a criterion's cold blind gold. The judge is the authenticated user (set server-side). */
export async function saveBlindLabels(body: {
  criterion_id: string;
  criterion_version: string;
  concepts: ConceptInput[];
}): Promise<BlindLabelsSaved> {
  const { data } = await apiClient.post(`${ROOT}/blind-labels`, body, { baseURL: V2_BASE });
  return data as BlindLabelsSaved;
}
